import { Suspense } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

export default function PrivacyPolicy() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#2B6777] to-[#52AB98]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90">
              Effective Date: January 5, 2026
            </p>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6">
            <div className="prose prose-lg max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <p className="text-lg text-gray-700 leading-relaxed">
                  SnapWindows respects your privacy and is committed to protecting your personal information. 
                  This Privacy Policy explains how we collect, use, and share your information when you visit{' '}
                  <strong>www.snapwindows.ai</strong>
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">1</span>
                  Information We Collect
                </h2>
                
                <div className="ml-13 space-y-6">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">a. Information You Provide</h3>
                    <p className="text-gray-700 mb-3">We may collect personal information such as:</p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li>Name, email address, and phone number (for example, when filling out a contact, quote request, or appointment form)</li>
                      <li>Project details or preferences submitted via forms, phone calls, emails, or text messages</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">b. Communications Data</h3>
                    <p className="text-gray-700">
                      If you opt in to receive SMS or email communications, we may collect and maintain records of your consent, 
                      messaging preferences, and opt-out activity.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">c. Automatically Collected Information</h3>
                    <p className="text-gray-700 mb-3">
                      We may automatically collect certain information when you visit our Website, including:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 text-gray-700">
                      <li>IP address</li>
                      <li>Browser type</li>
                      <li>Pages visited</li>
                      <li>Date and time of access</li>
                    </ul>
                    <p className="text-gray-700 mt-3">
                      This information helps us improve Website performance and user experience.
                    </p>
                  </div>
                </div>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">2</span>
                  How We Use Your Information
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700 mb-3">We may use the information we collect to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Respond to inquiries, service requests, or appointment scheduling</li>
                    <li>Send service-related updates, appointment confirmations, and reminders</li>
                    <li>Send SMS or email communications that you have explicitly opted in to receive</li>
                    <li>Improve our Website functionality, services, and customer experience</li>
                    <li>Maintain compliance records related to consent and opt-out requests</li>
                    <li>Comply with legal obligations and protect our business and users</li>
                  </ul>
                </div>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">3</span>
                  Sharing of Information
                </h2>
                
                <div className="ml-13 space-y-4">
                  <p className="text-gray-900 font-semibold">
                    We do not sell or rent your personal information.
                  </p>
                  <p className="text-gray-700">
                    No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. 
                    Information sharing to subcontractors in support services, such as customer service, is permitted. 
                    All other use case categories exclude text messaging originator opt-in data and consent; this information 
                    will not be shared with any third parties.
                  </p>
                  <p className="text-gray-700">
                    We may share limited information only in the following circumstances:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>With service providers or subcontractors who assist us with operational support such as customer service, 
                    scheduling, analytics, or communications delivery, solely to perform services on our behalf</li>
                    <li>With legal authorities if required to comply with applicable laws or legal processes</li>
                  </ul>
                </div>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">4</span>
                  Cookies
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700">
                    Our Website may use cookies and similar technologies to improve functionality and user experience. 
                    You can control cookies through your browser settings; however, some features of the Website may not 
                    function properly if cookies are disabled.
                  </p>
                </div>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">5</span>
                  Data Security
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700">
                    We implement reasonable administrative, technical, and physical safeguards to protect your personal information. 
                    However, no method of transmission over the internet or electronic storage is completely secure.
                  </p>
                </div>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">6</span>
                  Your Rights and Choices
                </h2>
                
                <div className="ml-13 space-y-4">
                  <p className="text-gray-700">
                    Depending on your location and applicable law, you may have the right to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Access or correct your personal information</li>
                    <li>Request deletion of your personal information</li>
                    <li>Opt out of marketing communications</li>
                  </ul>
                  <p className="text-gray-700">
                    You may exercise these rights by contacting us at{' '}
                    <a href="mailto:Snapwindowsai@gmail.com" className="text-[#52AB98] hover:text-[#2B6777] font-semibold cursor-pointer">
                      Snapwindowsai@gmail.com
                    </a>
                  </p>
                </div>
              </div>

              {/* Section 7 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">7</span>
                  Third-Party Tools
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700">
                    We may use third-party tools such as analytics platforms or customer relationship management software to help 
                    analyze Website traffic or manage customer inquiries. These tools process information in accordance with their 
                    own privacy policies and solely to support our services.
                  </p>
                </div>
              </div>

              {/* Section 8 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">8</span>
                  Children's Privacy
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700">
                    Our Website and services are not intended for children under the age of 13, and we do not knowingly collect 
                    personal information from children.
                  </p>
                </div>
              </div>

              {/* Section 9 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">9</span>
                  Changes to This Privacy Policy
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700">
                    We reserve the right to update this Privacy Policy at any time. The updated version will be posted on this page 
                    with a revised effective date.
                  </p>
                </div>
              </div>

              {/* Section 10 */}
              <div className="mb-12">
                <h2 className="text-3xl font-heading font-bold text-[#2B6777] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-[#52AB98] text-white rounded-full flex items-center justify-center text-lg shrink-0">10</span>
                  Contact Us
                </h2>
                
                <div className="ml-13">
                  <p className="text-gray-700 mb-4">
                    If you have any questions about this Privacy Policy, please contact us at:
                  </p>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <p className="text-gray-900 font-semibold text-lg mb-3">SnapWindows.Ai</p>
                    <div className="space-y-2 text-gray-700">
                      <p className="flex items-start gap-2">
                        <i className="ri-map-pin-line text-[#52AB98] mt-1"></i>
                        <span>3005 Mechanicsville Turnpike</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <i className="ri-phone-line text-[#52AB98] mt-1"></i>
                        <a href="tel:+18043622555" className="hover:text-[#52AB98] cursor-pointer">
                          (804) 362-2555
                        </a>
                      </p>
                      <p className="flex items-start gap-2">
                        <i className="ri-mail-line text-[#52AB98] mt-1"></i>
                        <a href="mailto:Snapwindowsai@gmail.com" className="hover:text-[#52AB98] cursor-pointer">
                          Snapwindowsai@gmail.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-br from-[#2B6777] to-[#52AB98]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Your privacy is protected. Get your free proposal today.
            </p>
            <a
              href="https://api.leadconnectorhq.com/widget/form/0ZsIY0jYLaF8aXm2FeXI"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-10 py-4 bg-[#D98CA3] hover:bg-[#c97a91] text-white text-lg font-bold rounded-2xl transition-all cursor-pointer whitespace-nowrap shadow-xl hover:shadow-2xl hover:scale-[1.02] transform"
            >
              Get My Free Proposal
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}